import {AxiosError} from 'axios'

import {BusinessLineType, Dictionary} from '../../common/types'
import {Order} from '../../Pages/Order/Order.types'

import {OrderRequestStatus, ShippingType} from './OrderIntake.enums'

export enum QuantityType {
  TruckCapacity = 'truckCapacity',
  Load = 'load'
}

export enum TimeFormatValues {
  AM = 'AM',
  PM = 'PM'
}

export enum TimePickerType {
  Earliest,
  Latest
}

// GET Optimized Options
export type PlantDto = {
  plantId?: string
  street?: string
  postalCode?: string
  city?: string
  state?: string
  country?: string
  plantName?: string
  plantNumber?: string
  configurableSlots: ConfigurableTimeSlot[]
}

export type OptimizedMaterialDto = {
  materialId?: string
  materialNumber?: string
  materialEnteredNumber?: string
  materialDescription?: string
  customerMaterialDescription?: string
  steDescription?: string
  recipeNumber?: string
  materialDescriptionLong?: string
  plantNumber?: string
  materialEnteredDescription?: string
  contractItemDescription?: string
  contractNumber?: string
  contractItemPositionNumber?: string
  businessLine?: BusinessLineType
  quantityType?: QuantityType
  poNumber?: string
  // optimized
  invalid?: boolean
  deactivationReason: string
  canDisableEmailSending?: boolean
  shippingType: ShippingType
  timeZone?: string
  isCarbonSavingProduct?: boolean
  bomMaterials?: BomMaterial[]
  packagingType?: 'V' | 'L' // 'V' for bagged, 'L' for (everything else) bulk
  orgUnitId?: string
}
export type ContractValidityPeriodDto = {
  contractValidityStartDate: string
  contractValidityEndDate: string
}
export type ContractUnitDto = {
  siteNumber: string
  siteName?: string
  street?: string
  postalCode?: string
  city?: string
  state?: string
  country?: string
  customerNumber?: string
  // customerReference?: string
  driverInstructions?: string
  contact: Contact
  timeZone?: string
  materials: OptimizedMaterialDto[]
  contractValidityPeriods: Record<ShippingType, ContractValidityPeriodDto>
  truckCapacity: Record<ShippingType, TruckCapacity>
}
export type OptimizedOption = {
  plant: PlantDto
  dateRange: Record<ShippingType, DateRange>
  businessHours: Record<ShippingType, BusinessHours>
  defaultDeliveryWindow: Record<ShippingType, DefaultDeliveryWindow>
  dispatchPhoneNumbers: string[]
  contractUnit: ContractUnitDto[]
}

export type ContractUnitV2Dto = {
  contractNumber: string
  orgUnitId: string
  customerNumber: string
  materials: OptimizedMaterialDto[]
  contractValidityPeriods: Record<ShippingType, ContractValidityPeriodDto>
  driverInstructions?: string
  salesOffice?: string
  customerId?: string
}

export type BusinessDayV2Dto = {
  dayOfWeek: string
  leadTime: number
  cutOffTime?: string
  autoConfirmLeadTime?: number
}

export type BusinessHoursV2Dto = {
  earliestPossibleLoadTime: string
  latestPossibleLoadTime: string
  defaultEarliestLoadTime: string
  defaultLatestLoadTime: string
  minInterval: string
  minFixTimeOrderInterval: string
  timeChangeInterval: string
  cutoffTimeThreshold: string
  timeZone: string
}

export type TruckCapacityV2 = {
  default: number
  max: number
  min: number
  uom: string
  step: number
  allowedDecimals: number
  isIncrementRounded: boolean
}

export type ConfigurationDto = {
  salesOrganization?: string
  shippingType: ShippingType
  validOrderRangeAfterCutoffTimeFrom?: string
  validOrderRangeAfterCutoffTimeTo?: string
  dateExceptions?: DateRangeExceptions
  allowedBusinessDays?: BusinessDayV2Dto[]
  allowedBusinessHours?: BusinessHoursV2Dto
  allowedTruckCapacity?: TruckCapacityV2
}

export type PumpSizeDto = {
  code: string
  size: number
  leadTime: number
}

export type HoseOptionDto = {
  minLength: number
  maxLength: number
  stepLength: number
}

export type PumpConstraintUnitDto = {
  pumpSize: string
  hoseSize: string
}

export type PumpConstraintDefaultSettingsDto = {
  pumpSize: number
  hoseLength: number
  canLargerPumpFit?: boolean
  isPossibleToCleanPumpOnSite?: boolean
}

export type PumpConstraintDto = {
  isEnabled: boolean
  availablePumpSizes: PumpSizeDto[]
  hoseOptions: HoseOptionDto
  units: PumpConstraintUnitDto
  defaultSettings: PumpConstraintDefaultSettingsDto
}

export type RetarderDto = {
  isEnabled: boolean
  minRetarder?: number
  maxRetarder?: number
  stepRetarder?: number
  retarderUom?: string
}

export type ScenarioDto = {
  loadSpace: boolean
  flowRate: boolean
  timeOnJobSite: boolean
}

export type QuantityDto = {
  isEnabled: boolean
  min?: number
  max?: number
  step?: number
  uom?: string
  default?: number
}

export type TruckDto = {
  defaultTruckSize: number
  isEnabled: boolean
}

export type QuantityChangeExpectedDto = {
  isEnabled: boolean
}

export type IsTestBookedDto = {
  isEnabled: boolean
}

export type UnloadingMethodDto = {
  isEnabled: boolean
  default: string[]
  salesOfficeUnloadingMethods: Record<string, string[]>
}

export type LoadSpacingDto = {
  isEnabled: boolean
  scenarios: Record<string, ScenarioDto>
  loadSpace: QuantityDto
  flowRate: QuantityDto
  timeOnJobSite: QuantityDto
}

export type ConstraintDto = {
  quantity: QuantityDto
  loadSpacing: LoadSpacingDto
  trucks: TruckDto
  quantityChangeExpected: QuantityChangeExpectedDto
  isTestBooked: IsTestBookedDto
  unloadingMethods: UnloadingMethodDto
  pumpConstraints: PumpConstraintDto
  retarder: RetarderDto
}

export type OptionsV2 = {
  plants: PlantDto[]
  sites: ShippingAddress[]
  contracts: ContractUnitV2Dto[]
  configurations: ConfigurationDto[]
  constraints: ConstraintDto[]
}

// GET Request Payload
export interface OrderIntakeMaterialOptionPayload {
  shippingAddress: ShippingAddress
  material: Material
  dateRange: DateRange
  deactivationReason: string
  dispatchPhoneNumbers: string[]
  driverInstructions?: string
  invalid: boolean
  customerNumber: string
  customerId?: string
  customerReference: string
  contact: Contact
  truckCapacity: TruckCapacity
  plant: Plant
  shippingType: ShippingType
  businessHours: BusinessHours
  defaultDeliveryWindow: DefaultDeliveryWindow
  canDisableEmailSending: boolean
  packagingType?: 'V' | 'L' // 'V' for bagged, 'L' for (everything else) bulk
}

// POST Request Payload

export type OrderIntakePayload = {
  siteNumber: string
  materialNumber: string
  materialDescription?: string
  materialEnteredNumber: string
  bomMaterialNumber?: string
  bomMaterialPositionNumber?: string
  customerReference: string
  contractItemPositionNumber: string
  contractNumber: string
  deliveryDate: string
  deliveryTime: DeliveryTime
  shippingType: ShippingType
  capacity: CapacityRequest
  additionalDriverInfo?: AdditionalDriverInfo
  isSendingConfirmationEmailUnChecked: boolean
  contact: Contact
  haulerInfo?: HaulerInfo
  customerId: string
  businessLineCode: string
  plantName: string
  plantNumber: string
  slot: SlotSiloConfig // todo: think of a better name for the type, match the backend type
  orderAllocatorInstructions: string
}

export type V2OrderIntakePayload = {
  country: string
  businessLine: string
  orgUnitId: string
  customerId: string
  siteId: string
  contactInformation: ContactInformation
  customerReference?: string
  shippingType: ShippingType
  allocatorInstructions?: string
  instructions?: string
  haulerInfo?: HaulerInfo
  orderCreateNotes?: string
  contractNumber: string
  materialNumber: string
  contractItem: string
  capacity: CapacityRequest
  scheduleStartDate: string
  scheduleEndDate?: string
  slot: SlotSiloConfig
}

export interface ContactInformation {
  orderPlacerName: string
  orderPlacerNumber: string
  siteContactName?: string
  siteContactNumber?: string
  carbonCopyEmail?: string
}

export interface SlotSiloConfig {
  loadingStation: string
  siloNumber: string
  externalReference: string
  plantId?: string
  materialNumber?: string
}

export type PersistedOrderRequest = Pick<
  OrderIntakePayload,
  | 'additionalDriverInfo'
  | 'capacity'
  | 'customerReference'
  | 'deliveryTime'
  | 'materialNumber'
  | 'materialDescription'
  | 'materialEnteredNumber'
  | 'contractItemPositionNumber'
  | 'isSendingConfirmationEmailUnChecked'
  | 'plantName'
  | 'plantNumber'
  | 'contractNumber'
>

export interface OrderRequest {
  isNoDatesAvailable: boolean
  isDateChange: boolean
  isMaterialHighlighted: boolean
  initialDeliveryDate: string
  initialDeliveryTime: DeliveryTime
  initialTruckQuantity: number
  initialFullName: string
  payload: OrderIntakePayload
  isDateNotAvailable?: boolean
  timeTrackingData?: TimeSelectorTrackingOrderEvent[]
}

export interface BusinessHours {
  cutOffTimeThreshold: string
  earliestPossible: string
  latestPossible: string
  minInterval: string
  minFixIntervalTime: string
  timeChangeInterval: string
  timeZone?: string
}

// Response Payload

export interface OrderRequestResponse {
  businessLine: string
  countryId: string
  createdBy: string
  createdOn: string
  customerNumber: string
  isSendingConfirmationEmailUnChecked: false
  modifiedOn: string
  orgUnitId: string
  requestData: RequestData
  requestId: string
  siteNumber: string
  status: OrderRequestStatus
  type: string
}

export interface OrderRequestGroupSuccessResponse {
  index: number
  result: OrderRequestResponse
  isSuccess: boolean
  delivery: OrderRequest
}

export interface OrderRequestGroupErrorResponse {
  index: number
  result: AxiosError
  isSuccess: boolean
  delivery: OrderRequest
}

export interface OrderRequestGroupResponse {
  index: number
  result: AxiosError | OrderRequestResponse
  isSuccess: boolean
  delivery: OrderRequest
}

export interface OrdersFormValidityState {
  isFormInvalid: boolean
  index: number
}

export interface AdditionalDriverInfo {
  driverInstructions?: string
}

export interface HaulerInfo {
  truckLicensePlate?: string
  trailerLicensePlate?: string
  driverName?: string
  driverPhoneNumber?: string
}

export interface RequestData {
  plant: Plant
  contact: Contact
  material: Material
  quantity: number
  quantityUom: string
  deliveryDate: string
  deliveryTime: DeliveryTime
  shippingType: ShippingType
  customerNumber: string
  shippingAddress: ShippingAddress
  customerReference: string
  additionalDriverInfo: AdditionalDriverInfo
  haulerInfo?: HaulerInfo
}

export interface OrderIntakeOption {
  shippingAddress: ShippingAddress
  contact: Contact
  materials: Dictionary<OrderIntakeMaterialOptionPayload[]>
  canDisableEmailSending: boolean
  driverInstructions?: string
}
export type OrderIntakeOptions = Dictionary<OrderIntakeOption>

export interface Contact {
  mainPhone?: string
  fullName?: string
  backupPhone?: string
  siteContactName?: string
  carbonCopyEmail?: string
  dispatchPhone?: string
}

export interface OrderCancellation {
  cancelReasonNotes: string
  checkSum: string | undefined
  timeZone: string
}

export interface TruckCapacity {
  capacity: number
  capacityUom: string
  maxCapacity: number
  minCapacity: number
  shortLoadThreshold: number
  quantityChangeIncrement?: number
  numberOfDecimals?: number
  isRoundToChangeIncrement?: boolean
}

export interface CapacityRequest {
  quantity: number
  quantityType: QuantityType
}

export type ConfigurableTimeSlot = {
  plantId: string
  date: string
  slotConfig: SlotConfiguration
  slotStatus: SlotStatus
}

export interface SlotConfiguration {
  id: string
  country: string
  slotName: string
  slotStart: string
  slotEnd: string
  isAlwaysFull: boolean
  slotPosition: number
}

export interface SlotStatus {
  isBlockingDelivery: boolean
}

export interface Plant {
  plantId?: string
  city: string
  country: string
  plantName: string
  plantNumber: string
  postalCode: string
  state: string
  street: string
  configurableSlots?: ConfigurableTimeSlot[]
}

export interface SalesOfficeData {
  country: string
  region: string
  businessLine: string
  nationalFlg: boolean
  salesOrganization: string
  companyName: string
  plant: string
  productCategoryCode: string
}

export interface OrderCancellationOptions {
  businessDays: OrderCancellationBusinessDay[]
  businessHoursOptions: BusinessHours
  isBusinessLineAllowed: boolean
}

export interface ShippingAddress {
  siteId?: string
  siteNumber: string
  siteName: string
  street: string
  postalCode: string
  city: string
  state: string
  country?: string
  contractNumbers?: string[]
  contacts?: Contact[]
}

export interface Material {
  materialId?: string
  materialEnteredNumber: string
  materialNumber: string
  materialDescription: string
  businessLine: BusinessLineType
  quantityType: QuantityType
  contractItemPositionNumber: string
  contractNumber: string
  isCarbonSavingProduct?: boolean
  bomMaterials?: BomMaterial[]
  packagingType?: 'V' | 'L' // 'V' for bagged, 'L' for (everything else) bulk
  orgUnitId?: string
}

export interface BomMaterial {
  plantNumber: string
  parentMaterialNumber: string
  bomMaterialDescription: string
  bomMaterialNumber: string
  bomMaterialPositionNumber: string
  quantity: number
}

// Date and time related
export interface CutOffTime {
  dates: string[]
  timestamp: string
}

export interface DateRangeExceptions {
  weekends?: string[]
  publicHolidays?: PublicHoliday[]
  excludedDates?: string[]
  excludedDays?: string[]
}

export interface BusinessDay {
  dayOfWeek: string
  leadTime: number
  cutOffTime?: CutOffTime
  cancellationLeadTime?: number
  cancellationCutOffTime?: string
}

export interface OrderCancellationBusinessDay {
  dayOfWeek: string
  leadTime: number
  cutOffTime?: string
  cancellationLeadTime: number
  cancellationCutOffTime?: string
}

interface PublicHoliday {
  name: string
  date: string
}

export interface DateRange {
  from: string
  to: string
  fromAfterCutoff: string
  toAfterCutoff: string
  exceptions?: DateRangeExceptions
  businessDays?: BusinessDay[]
}

export interface DeliveryTime {
  earliest: string
  latest: string
}

export interface DefaultDeliveryWindow {
  defaultEarliestLoadTime: string
  defaultLatestLoadTime: string
}

// Order Form
export interface BulkCementOrderIntakeStateType {
  selectedSite?: OrderIntakeOption
  selectedSiteCollect?: OrderIntakeOption
  deliveries: OrderRequest[]
  collections: OrderRequest[]
  orderSummary: OrderRequestResponse[]
  isReset: boolean
}

export interface OrderRequests {
  isFetching: boolean
  error: Error | null
  orderRequests: OrderRequestResponse[]
}

export interface MaterialDelivery {
  id: string
  index: number
  dateAndTime: string
  materialDescription: string | null | undefined
  quantity: number
  plant: string | null | undefined
}

export interface GroupedMaterialDeliveries {
  headline: MaterialDeliveryDescription
  summaryItems: MaterialDelivery[]
}

export interface MaterialDeliveryDescription {
  number: string
  quantity: number
  capacityUom: string | undefined
  description: string | null | undefined
  isCarbonSavingProduct?: boolean
}

export interface GroupedPlacedDeliveries {
  headline: MaterialDeliveryDescription
  orders: OrderIntakePayload[]
  groupedOrderSummary: OrderRequestGroupResponse[]
}

export type ActiveMaterialDeliveryItem =
  | 'dateFilter'
  | 'timePicker'
  | 'haulerInfo'
  | 'poNumber'
  | 'details'
export type ActiveMaterialEditorMode = 'edit' | 'add'

export type TimeSelectorTrackingEventType =
  | '<earliest'
  | '>earliest'
  | '<latest'
  | '>latest'
  | 'earliestInputField'
  | 'latestInputField'

export type OptionSelectInputChangeType = 'decrease' | 'increase' | 'inputField'

export interface TimeSelectorTrackingOrderEvent {
  orderSessionEventId: string
  cardId: string
  slotManaged: boolean
  startEditingTimeStamp: string
  events: Dictionary<number>
  applyChangesTimeStamp: string
}

export interface RequestForQuoteResponse {
  requestId: string
  status: string
}

export interface AddingNewMaterialMetadata {
  isAddingNewMaterial: boolean
  newMaterialIndex: number
}

export interface LabelValueType {
  label: string
  value: string
}

export interface LabelValueCodeType {
  label: string
  value: string
  code: string
}

export interface NotificationSettings {
  type: string
  option: string
  isActive: boolean
}

export interface OrderCancellationButtonParams {
  order: Order
  isCancellationRequestSent: boolean
  isBusinessLineAllowed: boolean | undefined
  isCancellationTimeOver: boolean
  isCancellationRequestCompleted: boolean
  isCancellationRequestNotFound: boolean
  isOrderStatusCancelled: boolean
  isErrorCancellationOptions: boolean
  isErrorCancellationRequestStatus: boolean
  isCancelRequestFailedOnCheckSum: boolean
  cancellationInfoText: string
  orderDeliveriesData: any
  getFeature: (featureName: string) => boolean
}
